import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderTree, ImagePlus, Loader2, Pencil, Plus, Power, Tag, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const INPUT = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

function TaxModal({ title, sub, initial, onClose, onSave, busy, testid, showDescription, imageLabel }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [image, setImage] = useState(initial?.image ?? initial?.logo ?? "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      setImage(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid={testid}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#F1EDE3] p-6" data-lenis-prevent>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            {sub && <p className="mt-0.5 text-xs text-neutral-500">{sub}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testid}-name`} className={INPUT} placeholder="e.g. Aluminium Foil Roll" />
        {showDescription && (
          <>
            <label className="mb-1 mt-3 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid={`${testid}-description`} className={INPUT} placeholder="Optional" />
          </>
        )}

        <label className="mb-1 mt-3 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{imageLabel}</label>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          data-testid={`${testid}-image-file`} onChange={(e) => upload(e.target.files?.[0])} />
        {image ? (
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`${testid}-image-preview`}>
            <img src={image} alt="Preview" className="h-14 w-14 rounded-lg bg-white/5 object-contain" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] text-neutral-500">{image}</p>
              <div className="mt-1.5 flex gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid={`${testid}-image-replace`}
                  className="rounded-lg border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
                  Replace
                </button>
                <button onClick={() => setImage("")}
                  data-testid={`${testid}-image-remove`}
                  className="rounded-lg border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-neutral-300 hover:border-red-500/50 hover:text-red-400">
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid={`${testid}-image-upload`}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-black/40 py-4 text-xs font-semibold text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload image (JPG/PNG/WEBP)"}
          </button>
        )}

        <button onClick={() => onSave({ name: name.trim(), description: description.trim(), image })}
          disabled={busy || uploading || name.trim().length < 2} data-testid={`${testid}-save`}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // {kind: 'category'|'subcategory'|'brand', mode:'create'|'edit', item, parent}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load categories")));
  }, []);
  useEffect(load, [load]);

  const save = async ({ name, description, image }) => {
    setBusy(true);
    try {
      const { kind, mode, item, parent } = modal;
      if (kind === "brand" || kind === "subbrand") {
        if (mode === "create") await api.post("/admin/brands", { name, logo: image, parent_id: kind === "subbrand" ? parent.id : null });
        else await api.put(`/admin/brands/${item.id}`, { name, logo: image });
      } else if (mode === "create") {
        await api.post("/admin/categories", { name, description, parent_id: parent?.id || null, image });
      } else {
        await api.put(`/admin/categories/${item.id}`, { name, description, image });
      }
      toast.success("Saved");
      setModal(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (kind, item) => {
    try {
      if (kind === "brand" || kind === "subbrand") await api.put(`/admin/brands/${item.id}`, { active: !item.active });
      else await api.post(`/admin/categories/${item.id}/toggle`);
      toast.success(item.active ? "Deactivated" : "Activated");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update"));
    }
  };

  const remove = async (kind, item) => {
    const label = kind === "subbrand" ? "sub-brand" : kind;
    if (!window.confirm(`Permanently delete the ${label} "${item.name}"? This cannot be undone.`)) return;
    try {
      if (kind === "brand" || kind === "subbrand") await api.delete(`/admin/brands/${item.id}`);
      else await api.delete(`/admin/categories/${item.id}`);
      toast.success(`"${item.name}" deleted`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't delete"));
    }
  };

  const Row = ({ item, kind, parent, sub }) => {
    const thumb = item.image || item.logo;
    return (
    <div className={`flex items-center justify-between gap-3 px-4 py-3 ${sub ? "border-l-2 border-amber-500/20 pl-6" : ""}`}
      data-testid={`cat-row-${item.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        {thumb ? (
          <img src={thumb} alt={item.name} data-testid={`cat-thumb-${item.id}`}
            className="h-9 w-9 shrink-0 rounded-lg border border-white/10 bg-white/5 object-contain" />
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-dashed border-white/10 text-neutral-700">
            {kind === "brand" ? <Tag className="h-3.5 w-3.5" /> : <FolderTree className="h-3.5 w-3.5" />}
          </span>
        )}
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${item.active ? "text-white" : "text-neutral-600 line-through"}`}>
          {item.name}
          <span className="ml-2 text-[10px] font-medium text-neutral-600">{item.product_count ?? ""} {item.product_count !== undefined ? "product(s)" : ""}</span>
        </p>
        <p className="text-[11px] text-neutral-600">{item.slug || ""}{item.description ? ` · ${item.description}` : ""}</p>
      </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${item.active ? "border-emerald-500/30 text-emerald-400" : "border-white/10 text-neutral-600"}`}
          data-testid={`cat-status-${item.id}`}>{item.active ? "Active" : "Inactive"}</span>
        <button onClick={() => setModal({ kind, mode: "edit", item, parent })} data-testid={`cat-edit-${item.id}`}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:border-amber-500/50 hover:text-amber-400" aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => toggle(kind, item)} data-testid={`cat-toggle-${item.id}`}
          className={`flex h-8 w-8 items-center justify-center rounded-lg border ${item.active ? "border-white/10 text-neutral-400 hover:text-red-300" : "border-emerald-500/30 text-emerald-500"}`}
          aria-label={item.active ? "Deactivate" : "Activate"}>
          <Power className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => remove(kind, item)} data-testid={`cat-delete-${item.id}`}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-red-500/50 hover:text-red-400"
          aria-label="Delete">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
    );
  };

  return (
    <div data-testid="admin-categories" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <FolderTree className="h-6 w-6 text-amber-400" /> Categories
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Categories and subcategories power the shop navigation and product filters. Deactivating never hides products.
          </p>
        </div>
        <button onClick={() => setModal({ kind: "category", mode: "create" })} data-testid="cat-add"
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> Add Category
        </button>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2" data-testid="cat-tree">
            {data.categories.length === 0 && (
              <p className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-12 text-center text-sm text-neutral-500" data-testid="cat-empty">
                No categories yet.
              </p>
            )}
            {data.categories.map((c) => (
              <div key={c.id} className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
                <Row item={c} kind="category" />
                {c.subcategories.map((s) => <Row key={s.id} item={s} kind="subcategory" parent={c} sub />)}
                <button onClick={() => setModal({ kind: "subcategory", mode: "create", parent: c })}
                  data-testid={`cat-add-sub-${c.id}`}
                  className="flex w-full items-center gap-2 border-t border-white/[0.05] px-4 py-2.5 text-xs font-bold text-neutral-500 hover:text-amber-400">
                  <Plus className="h-3.5 w-3.5" /> Add subcategory
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA]" data-testid="brands-card">
            <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-3">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                <Tag className="h-4 w-4 text-amber-400" /> Brands
              </h2>
              <button onClick={() => setModal({ kind: "brand", mode: "create" })} data-testid="brand-add"
                className="flex h-8 items-center gap-1.5 rounded-full border border-amber-500/40 px-3 text-[11px] font-bold text-amber-300 hover:bg-amber-500/10">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            {data.brands.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-neutral-600" data-testid="brands-empty">No brands yet.</p>
            ) : (
              data.brands.filter((b) => !b.parent_id).map((b) => (
                <div key={b.id} className="border-b border-white/[0.05] last:border-0">
                  <Row item={b} kind="brand" />
                  {data.brands.filter((s) => s.parent_id === b.id).map((s) => (
                    <Row key={s.id} item={s} kind="subbrand" parent={b} sub />
                  ))}
                  <button onClick={() => setModal({ kind: "subbrand", mode: "create", parent: b })}
                    data-testid={`brand-add-sub-${b.id}`}
                    className="flex w-full items-center gap-2 px-4 pb-2.5 pt-1 text-[11px] font-bold text-neutral-500 hover:text-amber-400">
                    <Plus className="h-3 w-3" /> Add sub-brand
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {modal && (
        <TaxModal
          testid="tax-modal"
          title={modal.mode === "create"
            ? (modal.kind === "brand" ? "Add brand" : modal.kind === "subbrand" ? `Add sub-brand · ${modal.parent.name}` : modal.kind === "subcategory" ? `Add subcategory · ${modal.parent.name}` : "Add category")
            : `Edit ${modal.kind === "subbrand" ? "sub-brand" : modal.kind}`}
          sub={modal.kind === "brand" || modal.kind === "subbrand" ? "Brand logos appear on the homepage brand rail and shop filters." : "Renaming updates every product using it automatically. The image shows on the homepage category grid."}
          initial={modal.item}
          showDescription={modal.kind !== "brand" && modal.kind !== "subbrand"}
          imageLabel={modal.kind === "brand" || modal.kind === "subbrand" ? "Brand Logo" : "Category Image"}
          busy={busy}
          onClose={() => setModal(null)}
          onSave={save}
        />
      )}
    </div>
  );
}
